import { SlideDTO } from '@superlekcja/shared'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { hex, SlideTheme, useSlideTheme } from './slide-theme'

/**
 * Jedna plansza slajdu w proporcji 16:9; rozmiar nadaje rodzic (szerokosc +
 * `@container`, bo typografia skaluje sie w `cqw`).
 *
 * Kolory ida ze `slide-theme.ts` STYLAMI INLINE, a nie klasami Tailwinda.
 * To celowe: ten sam obiekt palety zasila eksport do PPTX, wiec plansza na
 * ekranie i slajd w PowerPoincie nie moga sie rozjechac (patrz komentarz
 * w slide-theme.ts).
 *
 * `titleImageUrl` zamienia slajd tytulowy w pelnoekranowa okladke; bez niego
 * tytul zostaje wersja tekstowa.
 */
export function SlideContent({
  slide,
  className,
  titleImageUrl,
  isGenerating,
  theme,
}: {
  slide: SlideDTO
  className?: string
  titleImageUrl?: string
  isGenerating?: boolean
  /** nadpisanie motywu — prezenter i podglad przekazuja ten sam obiekt */
  theme?: SlideTheme
}) {
  const { t } = useTranslation()
  const active = useSlideTheme()
  const c = theme ?? active

  // Slajd, ktorego nie dalo sie odczytac ze strumienia. Tresc komunikatu bierze sie ze stanu
  // talii, wiec nie potrzeba osobnego pola w danych.
  if (slide.needsFix) {
    return (
      <div
        data-testid="slide-needs-fix"
        role="status"
        className={cn(
          'flex aspect-video w-full flex-col items-center justify-center gap-[1.5cqw] rounded-lg border-2 border-dashed px-[8%] text-center',
          className,
        )}
        style={{ borderColor: hex(c.muted) + '66', background: hex(c.softBg) }}
      >
        <span className="text-[4cqw]" aria-hidden>
          ⚠️
        </span>
        <p className="text-[2.4cqw] font-semibold" style={{ color: hex(c.muted) }}>
          {isGenerating ? t('slide-needs-fix-repairing') : t('slide-needs-fix-title')}
        </p>
        {!isGenerating && (
          <p className="text-[1.8cqw]" style={{ color: hex(c.muted) }}>
            {t('slide-needs-fix-hint')}
          </p>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn('aspect-video w-full overflow-hidden rounded-lg', className)}
      style={{ background: hex(c.surface), color: hex(c.ink) }}
    >
      {slide.layout === 'title' &&
        (titleImageUrl ? (
          <div className="relative flex h-full flex-col items-center justify-center px-[8%] text-center">
            <img src={titleImageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-linear-to-t from-black/75 via-black/45 to-black/35" />
            <div className="relative">
              <h1 className="text-[4.5cqw] font-bold text-white drop-shadow-lg">{slide.title}</h1>
              {slide.subtitle && (
                <p className="mt-[2cqw] text-[2.2cqw] text-white/90 drop-shadow-md">{slide.subtitle}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center px-[8%] text-center">
            <h1 className="text-[4.5cqw] font-bold" style={{ color: hex(c.primary) }}>
              {slide.title}
            </h1>
            {slide.subtitle && (
              <p className="mt-[2cqw] text-[2.2cqw]" style={{ color: hex(c.muted) }}>
                {slide.subtitle}
              </p>
            )}
          </div>
        ))}

      {slide.layout === 'section' && (
        <div
          className="flex h-full items-center justify-center px-[8%] text-center"
          style={{ background: hex(c.primary) }}
        >
          <h2 className="text-[4cqw] font-bold" style={{ color: hex(c.onPrimary) }}>
            {slide.title}
          </h2>
        </div>
      )}

      {slide.layout === 'bullets' && (
        <div className="flex h-full flex-col px-[6%] py-[5%]">
          <h2
            className="pb-[1.5cqw] text-[3.2cqw] font-bold"
            style={{ color: hex(c.primary), borderBottom: `0.5cqw solid ${hex(c.accent)}` }}
          >
            {slide.title}
          </h2>
          <ul className="mt-[3cqw] flex flex-col gap-[1.5cqw] text-[2.4cqw]">
            {slide.bullets?.map((b, i) => (
              <li key={i} className="flex gap-[1.5cqw]">
                <span
                  className="mt-[1.1cqw] h-[0.8cqw] w-[0.8cqw] shrink-0 rounded-full"
                  style={{ background: hex(c.bullet) }}
                  aria-hidden
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {slide.layout === 'question' && (
        <div className="flex h-full flex-col justify-center px-[6%]">
          <div
            className="text-[1.6cqw] font-bold uppercase tracking-[0.2em]"
            style={{ color: hex(c.primary) }}
          >
            {t('slide-question-label')}
          </div>
          <h2 className="mt-[1.5cqw] text-[3.4cqw] font-bold">{slide.title}</h2>
          {slide.hints && slide.hints.length > 0 && (
            <ul
              className="mt-[3cqw] flex flex-col gap-[1cqw] rounded-[1.5cqw] px-[3cqw] py-[2cqw] text-[2cqw]"
              style={{ background: hex(c.softBg), color: hex(c.muted) }}
            >
              {slide.hints.map((h, i) => (
                <li key={i} className="flex gap-[1.5cqw]">
                  <span
                    className="mt-[0.9cqw] h-[0.7cqw] w-[0.7cqw] shrink-0 rounded-full"
                    style={{ background: hex(c.bullet) }}
                    aria-hidden
                  />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
